import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  model,
  signal,
  viewChild
} from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";

import { debounceTime, Subscription, tap } from "rxjs";

import { DocumentTreeComponent } from "@documentation/components/sidebar/tabs/documents-list/document-tree/document-tree.component";
import { DocumentListItemModel } from "@documentation/models/document.model";
import { FolderModel, TreeNode, TreeNodeUnion } from "@documentation/models/folder.model";
import { DocumentHttpService } from "@documentation/services/document-http.service";
import { ChipComponent } from "@shared/components/chip/chip.component";
import { InputComponent } from "@shared/components/input/input.component";
import { BaseModalComponent } from "@shared/components/modal/base-modal.component";
import { ModalComponent } from "@shared/components/modal/modal.component";

@Component({
  selector: "component-document-tree-picker-modal",
  imports: [InputComponent, ModalComponent, DocumentTreeComponent, ChipComponent, FormsModule],
  templateUrl: "./document-tree-picker-modal.component.html",
  styleUrl: "./document-tree-picker-modal.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DocumentTreePickerModalComponent extends BaseModalComponent {
  private static readonly SEARCH_DEBOUNCE_TIME_MS = 300;

  readonly selectedDocuments = model<DocumentListItemModel[]>();
  readonly hiddenDocumentsIds = model<string[]>();

  protected readonly selectedDocumentsMap = signal<Map<string, DocumentListItemModel>>(new Map());
  protected readonly scrollContainerHasScroll = signal(false);
  protected readonly searchValue = signal<string>("");
  private readonly _debouncedSearchValue = signal<string>("");
  private readonly _root = signal<FolderModel | null>(null);
  private readonly _searchResults = signal<TreeNode[] | null>(null);

  protected readonly itemsToDisplay = computed(() => {
    return this.hasSearchValue() ? this._searchResults() : this._filteredRootNodes();
  });

  protected readonly initialMap = computed(() => {
    return new Map(this.selectedDocuments()?.map((doc) => [doc.id, doc]));
  });

  protected readonly hasSearchValue = computed(() => {
    const query = this._debouncedSearchValue();
    return !!query && query.trim().length > 0;
  });

  protected readonly hasChanges = computed(() => {
    const a = this.selectedDocumentsMap();
    const b = this.initialMap();

    if (a.size !== b.size) return true;

    for (const key of a.keys()) {
      if (!b.has(key)) return true;
    }

    return false;
  });

  private readonly _filteredRootNodes = computed(() => {
    const content = this._root()?.content;
    return content ? this.filterHiddenDocs(content) : null;
  });

  private readonly _scrollContainer = viewChild("scrollContainer", { read: ElementRef });
  private readonly _documentHttp = inject(DocumentHttpService);

  private _searchRequestSub?: Subscription;

  constructor() {
    super();

    this.loadRootFolder();
    this.debounceSearchValue();

    toObservable(this.selectedDocumentsMap).subscribe(() => {
      requestAnimationFrame(() => {
        const element = this._scrollContainer()?.nativeElement;
        this.scrollContainerHasScroll.set(element?.scrollWidth > element?.clientWidth);
      });
    });

    toObservable(this._debouncedSearchValue).subscribe(() => this.performSearch());

    effect(() => this.selectedDocumentsMap.set(this.initialMap()));
  }

  protected onRemoveDocument(doc: DocumentListItemModel): void {
    const updated = new Map(this.selectedDocumentsMap());
    updated.delete(doc.id);
    this.selectedDocumentsMap.set(updated);
  }

  protected onSubmit(): void {
    this.activeModal.close(this.hasChanges() ? [...this.selectedDocumentsMap().values()] : void 0);
  }

  private debounceSearchValue(): void {
    toObservable(this.searchValue)
      .pipe(
        tap((val) => (val === "" ? this._debouncedSearchValue.set("") : null)),
        debounceTime(DocumentTreePickerModalComponent.SEARCH_DEBOUNCE_TIME_MS)
      )
      .subscribe((searchValue) => this._debouncedSearchValue.set(searchValue));
  }

  private loadRootFolder(): void {
    this._documentHttp.getDocumentListItems().subscribe((root) => {
      this._root.set(root);
    });
  }

  private performSearch(): void {
    this._searchResults.set(null);
    this._searchRequestSub?.unsubscribe();

    if (!this.hasSearchValue()) return;

    this._searchRequestSub = this._documentHttp
      .searchDocuments(this._debouncedSearchValue().trim())
      .subscribe((res) => this._searchResults.set(this.filterHiddenDocs(res)));
  }

  private filterHiddenDocs(nodes: TreeNode[]): TreeNode[] {
    if (!this.hiddenDocumentsIds()?.length) return nodes;

    const filtered = this.filterTree(nodes, this.hiddenDocumentsIds()!);
    return TreeNodeUnion.fromPlain(filtered);
  }

  private filterTree(nodes: TreeNode[], hiddenIds: string[]): TreeNode[] {
    const result: TreeNode[] = [];

    for (const node of nodes) {
      if (node.type === "document") {
        // skip document
        if (hiddenIds.includes(node.id)) {
          continue;
        }
        result.push(node);
        continue;
      }

      // It’s a folder → recursively filter content
      const filteredContent = this.filterTree(node.content, hiddenIds);

      result.push({
        ...node,
        content: filteredContent
      });
    }

    return result;
  }
}
